import { useCallback, useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useKural } from '../hooks/useKural';
import { useGraphIndex } from '../hooks/useGraphIndex';
import { useLang } from '../hooks/useLang';
import { useFocusHistory } from '../hooks/useFocusHistory';
import ReadingPanel from '../components/ReadingPanel';
import FocusGraph from '../components/FocusGraph';
import GraphLegend from '../components/GraphLegend';
import GraphNodeList from '../components/GraphNodeList';
import Breadcrumb from '../components/Breadcrumb';
import './Focus.css';

export default function Focus() {
    const { n: nParam } = useParams<{ n: string }>();
    const parsed = Number(nParam);
    const n =
        Number.isInteger(parsed) && parsed >= 1 && parsed <= 1330 ? parsed : null;

    const [lang] = useLang();
    const graph = useGraphIndex();
    const kural = useKural(n);
    const { history } = useFocusHistory(kural.data?.n ?? null);
    const navigate = useNavigate();
    const { search } = useLocation();

    const onNavigate = useCallback(
        (target: { kind: 'kural' | 'theme'; id: number | string }) => {
            const path =
                target.kind === 'kural' ? `/kural/${target.id}` : `/theme/${target.id}`;
            navigate(`${path}${search}`);
        },
        [navigate, search]
    );

    // Compute the sibling / related / theme sets from the graph index up
    // front so the a11y node-list has the same data as the SVG graph.
    const groupings = useMemo(() => {
        if (!graph.data || !kural.data) return null;
        const focusEntry = graph.data.kurals[kural.data.n - 1];
        const adhigaram = graph.data.adhigarams.find(
            (a) => a.n === focusEntry.adhigaram_n
        );
        const kuralByN = new Map(graph.data.kurals.map((k) => [k.n, k]));
        const siblingNs = adhigaram
            ? adhigaram.kural_ns.filter((x) => x !== focusEntry.n)
            : [];
        const siblings = siblingNs
            .map((x) => kuralByN.get(x))
            .filter((k): k is NonNullable<typeof k> => k !== undefined);
        const related = focusEntry.related
            .map((r) => {
                const k = kuralByN.get(r.n);
                return k ? { kural: k, shared_tags: r.shared_tags } : null;
            })
            .filter((x): x is { kural: (typeof siblings)[number]; shared_tags: string[] } => x !== null);
        const themeBySlug = new Map(graph.data.themes.map((t) => [t.slug, t]));
        const themes = focusEntry.tags
            .map((slug) => themeBySlug.get(slug))
            .filter((t): t is NonNullable<typeof t> => t !== undefined);
        return { focusEntry, siblings, related, themes };
    }, [graph.data, kural.data]);

    if (n === null) {
        return <Navigate to="/" replace />;
    }

    if (graph.error || kural.error) {
        return (
            <div className="focus-error" role="alert">
                <p>
                    Sorry — we couldn't load{' '}
                    {graph.error ? 'the corpus index' : `kural ${n}`}. Refresh the page to
                    try again.
                </p>
            </div>
        );
    }

    if (graph.loading || kural.loading || !graph.data || !kural.data || !groupings) {
        return (
            <div className="focus-loading" aria-live="polite">
                <span className="focus-loading__dot" aria-hidden="true" />
                <span className="focus-loading__label">Loading kural {n}…</span>
            </div>
        );
    }

    const adhigaram = graph.data.adhigarams.find(
        (a) => a.n === kural.data!.adhigaram_n
    );
    const firstKuralN = adhigaram?.kural_ns[0] ?? n;

    return (
        <div className="focus">
            <Breadcrumb history={history} />

            <div className="focus__grid">
                <section className="focus__graph-panel" aria-label="Kural connections">
                    <FocusGraph
                        focus={groupings.focusEntry}
                        graph={graph.data}
                        lang={lang}
                        onNavigate={onNavigate}
                    />
                    <GraphLegend />
                    <GraphNodeList
                        focus={groupings.focusEntry}
                        siblings={groupings.siblings}
                        related={groupings.related}
                        themes={groupings.themes}
                        lang={lang}
                        onNavigate={onNavigate}
                    />
                </section>

                <section className="focus__reading" aria-label="Kural reading">
                    <ReadingPanel
                        kural={kural.data}
                        themes={graph.data.themes}
                        paals={graph.data.paals}
                        adhigaramFirstKuralN={firstKuralN}
                        lang={lang}
                    />
                </section>
            </div>
        </div>
    );
}
